/**
 * Pure helpers for opening a citation's PDF in-app. Kept free of React / DI so
 * they can be unit-tested in isolation; the wiring lives in the
 * `useCitationPdfOpener` hook.
 */

import type { Citation } from '../types';
import { citationDisplayKey } from './linkify-citations';

/** Same-origin backend proxy that resolves + streams citation PDFs. */
export const CITATION_PDF_ENDPOINT = '/api/copilot/orchestration/citation-pdf';

/** Same-origin backend proxy that fetches + sanitises a citation source page. */
export const CITATION_SOURCE_ENDPOINT =
  '/api/copilot/orchestration/citation-source';

/** The same-origin proxy URL an in-app source iframe loads, or null. */
export function buildCitationSourceProxyUrl(citation: Citation): string | null {
  const landing = citationLandingUrl(citation);
  if (!landing) return null;
  return `${CITATION_SOURCE_ENDPOINT}?url=${encodeURIComponent(landing)}`;
}

/**
 * Normalise a citation type to its canonical lowercase form. The backend emits
 * `'pmid'` while the frontend `CitationType` union historically used
 * `'pubmed'` — both must be treated as PubMed (matching `citationDisplayKey`).
 */
function normalizeCitationType(type: string | undefined): string {
  const t = (type ?? '').toLowerCase();
  return t === 'pubmed' ? 'pmid' : t;
}

/**
 * Build the citation-pdf endpoint URL for a citation: `?url=` when a direct
 * open-access PDF URL is known (arXiv / a citation carrying `pdfUrl`), else
 * `?type=&identifier=` so the backend can resolve it (Europe PMC for PMID,
 * Unpaywall for DOI). Returns null when the citation can't be opened in-app.
 */
export function buildCitationPdfRequest(citation: Citation): string | null {
  const pdfUrl = deriveCitationPdfUrl(citation);
  if (pdfUrl) {
    return `${CITATION_PDF_ENDPOINT}?url=${encodeURIComponent(pdfUrl)}`;
  }
  const resolverType = citationResolverType(citation.type);
  const id = citation.identifier?.trim();
  if (resolverType && id) {
    return `${CITATION_PDF_ENDPOINT}?type=${resolverType}&identifier=${encodeURIComponent(
      id
    )}`;
  }
  return null;
}

/**
 * Derive a direct open-access PDF URL for a citation when one is known without
 * a network round-trip:
 *   - an explicit `pdfUrl` (arXiv / Semantic Scholar / OpenAlex / resolver), or
 *   - an arXiv id → `https://arxiv.org/pdf/{id}`.
 * Returns `null` when resolution needs the backend (PMID / DOI) or no PDF
 * source is known.
 */
export function deriveCitationPdfUrl(citation: Citation): string | null {
  if (citation.pdfUrl) return citation.pdfUrl;
  const id = citation.identifier?.trim();
  if (!id) return null;
  if (normalizeCitationType(citation.type) === 'arxiv') {
    const clean = id.replace(/v\d+$/i, '');
    return `https://arxiv.org/pdf/${clean}`;
  }
  return null;
}

/**
 * Maps a frontend citation type to the identifier type the backend resolver
 * understands (`pmid` | `doi` | `arxiv`), or null when the citation can't be
 * resolved to an open-access PDF by identifier.
 */
export function citationResolverType(
  type: string | undefined
): 'pmid' | 'doi' | 'arxiv' | null {
  switch (normalizeCitationType(type)) {
    case 'pmid':
      return 'pmid';
    case 'doi':
      return 'doi';
    case 'arxiv':
      return 'arxiv';
    default:
      return null;
  }
}

/**
 * External landing page for a citation, used for the "View source" fallback
 * when no open-access PDF is available. Mirrors the URL scheme used by
 * `linkifyCitations` and the citations page's `identifierUrl`.
 */
export function citationLandingUrl(citation: Citation): string | null {
  if (citation.url) return citation.url;
  const id = citation.identifier?.trim();
  if (!id) return null;
  switch (normalizeCitationType(citation.type)) {
    case 'doi':
      return `https://doi.org/${id}`;
    case 'pmid':
      return `https://pubmed.ncbi.nlm.nih.gov/${id}/`;
    case 'arxiv':
      return `https://arxiv.org/abs/${id}`;
    default:
      return null;
  }
}

/**
 * Map a clicked citation-anchor href (as produced by `linkifyCitations`) back
 * to the originating `Citation`, so a delegated click handler on rendered
 * synthesis text can intercept the link and open the PDF in-app.
 */
export function matchCitationByHref(
  href: string,
  citations: Citation[]
): Citation | null {
  const parsed = parseCitationHref(href);
  if (!parsed) return null;
  const key = citationDisplayKey(parsed.type, parsed.identifier);
  for (const c of citations) {
    if (!c.identifier || !c.type) continue;
    if (citationDisplayKey(c.type, c.identifier) === key) return c;
  }
  return null;
}

/**
 * Parse a PubMed / DOI / arXiv URL into its citation type + identifier. Used
 * both to match rendered links back to known citations and to synthesise a
 * citation from a bare link (e.g. the numbered `[1]` links in a persisted doc).
 */
export function parseCitationHref(
  href: string
): { type: 'pmid' | 'doi' | 'arxiv'; identifier: string } | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const host = url.hostname;
  const segments = url.pathname.split('/').filter(Boolean);

  if (host.includes('pubmed.ncbi.nlm.nih.gov')) {
    const id = segments[0];
    return id && /^\d+$/.test(id) ? { type: 'pmid', identifier: id } : null;
  }
  if (host.endsWith('doi.org')) {
    const id = decodeURIComponent(url.pathname.replace(/^\//, ''));
    return id ? { type: 'doi', identifier: id } : null;
  }
  if (host.includes('arxiv.org')) {
    // /abs/{id} or /pdf/{id}(.pdf)
    const last = segments[segments.length - 1];
    const id = last?.replace(/\.pdf$/i, '');
    return id ? { type: 'arxiv', identifier: id } : null;
  }
  return null;
}

/**
 * Build a minimal `Citation` from a bare citation link, so a global click
 * interceptor can open any rendered PubMed/DOI/arXiv link (including the
 * numbered `[1]` links written into a persisted doc) in-app. Returns null for
 * non-citation links.
 */
export function citationFromHref(href: string): Citation | null {
  const parsed = parseCitationHref(href);
  if (!parsed) return null;
  return {
    id: href,
    // 'pmid' is the backend's value; not in the frontend union but handled at runtime.
    type: parsed.type as Citation['type'],
    identifier: parsed.identifier,
    url: href,
    text: '',
    verified: false,
  };
}

/** A filesystem-safe filename for the cached library PDF. */
export function citationPdfFileName(citation: Citation): string {
  const base =
    citation.title?.trim() ||
    citation.identifier?.trim() ||
    citation.id ||
    'citation';
  const safe = base.replace(/[\\/:*?"<>|]+/g, '_').slice(0, 120);
  return safe.toLowerCase().endsWith('.pdf') ? safe : `${safe}.pdf`;
}
